/**
 * Pure pagination helpers shared by the `Pagination` component and its consumers.
 *
 * The component is fully controlled: consumers own the current `page`/`pageSize`
 * and convert to the backend's `limit`/`offset` themselves. These helpers keep
 * the display maths in one testable place.
 */

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

/** Default rows per page for list screens that have no reason to differ. */
export const DEFAULT_PAGE_SIZE = 10;

/** A slot in the rendered pager: a page number or a gap marker. */
export type PageToken = number | "start-ellipsis" | "end-ellipsis";

export interface PaginationRangeInput {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. */
  totalPages: number;
  /** Pages shown either side of the current page. Default 1. */
  siblingCount?: number;
  /** Pages pinned at each end. Default 1. */
  boundaryCount?: number;
}

export interface ShowingRange {
  start: number;
  end: number;
  total: number;
}

/** Total pages for a count/size, never below 1 so the summary always reads sensibly. */
export function totalPages(totalCount: number, pageSize: number): number {
  if (pageSize <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(totalCount / pageSize));
}

/** Clamp a page into `[1, totalPages]`; NaN falls back to page 1. */
export function clampPage(page: number, totalPages: number): number {
  if (Number.isNaN(page)) {
    return 1;
  }
  const upperBound = Math.max(totalPages, 1);
  return Math.min(Math.max(page, 1), upperBound);
}

/**
 * The "Showing {start} to {end} of {total}" figures.
 * Returns zeroes when there are no items.
 */
export function getShowingRange(
  page: number,
  pageSize: number,
  totalCount: number,
): ShowingRange {
  if (totalCount <= 0 || pageSize <= 0) {
    return { start: 0, end: 0, total: Math.max(totalCount, 0) };
  }
  const safePage = clampPage(page, totalPages(totalCount, pageSize));
  const start = (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, totalCount);
  return { start, end, total: totalCount };
}

function range(start: number, end: number): number[] {
  const length = end - start + 1;
  return length > 0 ? Array.from({ length }, (_, index) => start + index) : [];
}

/**
 * Build the ordered list of page tokens to render, MUI-style: pinned boundary
 * pages at each end, a constant-width window of siblings around the current
 * page, and ellipsis markers where a gap is larger than one page (a single-page
 * gap renders the page itself instead of an ellipsis). The constant width means
 * the pager never shifts as you page through (e.g. `1 2 3 4 5 … 16` on page 1,
 * `1 … 7 8 9 … 16` in the middle). The two ellipsis tokens are distinct so they
 * double as stable React keys.
 */
export function getPaginationRange({
  page,
  totalPages: count,
  siblingCount = 1,
  boundaryCount = 1,
}: PaginationRangeInput): PageToken[] {
  if (count <= 0) {
    return [];
  }

  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(
    Math.max(count - boundaryCount + 1, boundaryCount + 1),
    count,
  );

  const siblingsStart = Math.max(
    Math.min(page - siblingCount, count - boundaryCount - siblingCount * 2 - 1),
    boundaryCount + 2,
  );

  const siblingsEnd = Math.min(
    Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
    endPages.length > 0 ? endPages[0]! - 2 : count - 1,
  );

  return [
    ...startPages,
    ...(siblingsStart > boundaryCount + 2
      ? (["start-ellipsis"] as PageToken[])
      : boundaryCount + 1 < count - boundaryCount
        ? [boundaryCount + 1]
        : []),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < count - boundaryCount - 1
      ? (["end-ellipsis"] as PageToken[])
      : count - boundaryCount > boundaryCount
        ? [count - boundaryCount]
        : []),
    ...endPages,
  ];
}
